import { useEffect, useMemo, useRef, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";
import {
  TbAlertCircle,
  TbCircleCheck,
  TbCloudUpload,
  TbDownload,
  TbEdit,
  TbFilter,
  TbInfoCircle,
  TbMail,
  TbPhone,
  TbPlus,
  TbRefresh,
  TbSearch,
  TbTrash,
  TbTruck,
  TbUserSearch,
  TbUsers,
  TbX,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-[9999] transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}>
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

const API_BASE_URL = "https://new.cajuu.app/api/carrier-leads";

const PIPELINE_STATUSES = [
  "Novo Lead",
  "Não Atendida",
  "Em Contato",
  "Retorno Agendado",
  "Qualificado",
  "Oferta Enviada",
  "Ativo",
  "Sem Interesse",
  "Descartado",
];

const LEAD_TYPES = ["Todos", "Carrier/Shipper", "Broker", "Carrier/Broker"];
const TEMPERATURES = ["Todos", "HOT", "WARM", "COLD"];

const emptyForm = {
  company: "",
  name: "",
  phone: "",
  email: "",
  mc_number: "",
  dot_number: "",
  current_location: "",
  city: "",
  state: "",
  zip: "",
  equipment: "",
  drivers_count: "",
  power_units: "",
  operating_status: "",
  usdot_status: "",
  authority_status: "",
  authority_warning: false,
  authority_warning_text: "",
  safety_rating: "",
  lead_type: "Carrier/Shipper",
  pipeline_status: "Novo Lead",
  source: "Manual",
  notes: "",
};

function CarrierLead() {
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [leadTypeFilter, setLeadTypeFilter] = useState("Todos");
  const [temperatureFilter, setTemperatureFilter] = useState("Todos");
  const [potentialOnly, setPotentialOnly] = useState(false);
  const [stateFilter, setStateFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, from: 0, to: 0, per_page: 10 });
  const [totalPages, setTotalPages] = useState(1);

  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [form, setForm] = useState(emptyForm);
  const [syncSearch, setSyncSearch] = useState("");
  const [syncDot, setSyncDot] = useState("");
  const [syncMc, setSyncMc] = useState("");

  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchLeads(currentPage);
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, statusFilter, leadTypeFilter, temperatureFilter, potentialOnly, stateFilter, dateFrom, dateTo, itemsPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && modalOpen) {
        closeModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [modalOpen]);

  const getAuthHeaders = (isMultipart = false) => {
    const token = localStorage.getItem("token");

    return {
      ...(isMultipart ? {} : { "Content-Type": "application/json" }),
      "Screen-Name": "/carrier-leads",
      Authorization: `Bearer ${token}`,
    };
  };

  const unwrapPayload = (response) => response?.data?.data ?? response?.data ?? {};
  const unwrapPaginator = (payload) => payload?.data?.data ? payload.data : payload;

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ message, type, id: Date.now() });
  };

  const fetchMeta = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/meta`, { headers: getAuthHeaders() });
      setMeta(unwrapPayload(response));
    } catch (error) {
      console.error("Error fetching carrier lead meta:", error);
    }
  };

  const fetchLeads = async (pageToFetch = 1) => {
    setLoading(true);

    try {
      const response = await axios.get(API_BASE_URL, {
        headers: getAuthHeaders(),
        params: {
          page: pageToFetch,
          per_page: itemsPerPage,
          search: debouncedSearchQuery || undefined,
          status: statusFilter !== "Todos" ? statusFilter : undefined,
          lead_type: leadTypeFilter !== "Todos" ? leadTypeFilter : undefined,
          temperature: temperatureFilter !== "Todos" ? temperatureFilter : undefined,
          potential: potentialOnly ? "1" : undefined,
          state: stateFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          sort_by: "updated_at",
          sort_direction: "desc",
        },
      });

      const payload = unwrapPayload(response);
      const paginator = unwrapPaginator(payload);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      setLeads(list);
      setTotalPages(paginator?.last_page || 1);
      setPaginationMeta({
        total: paginator?.total || list.length,
        from: paginator?.from || (list.length ? 1 : 0),
        to: paginator?.to || list.length,
        per_page: paginator?.per_page || itemsPerPage,
      });
    } catch (error) {
      console.error("Error fetching carrier leads:", error);
      setLeads([]);
      setTotalPages(1);
      setPaginationMeta({ total: 0, from: 0, to: 0, per_page: itemsPerPage });
      showSnackbar(error.response?.data?.message || error.response?.data?.details || "Erro ao carregar Leads Carrier", "error");
    } finally {
      setLoading(false);
    }
  };

  const statusCounts = useMemo(() => {
    const counts = { Todos: meta?.total || paginationMeta.total || 0 };
    PIPELINE_STATUSES.forEach((status) => {
      counts[status] = meta?.by_status?.[status] || 0;
    });
    return counts;
  }, [meta, paginationMeta.total]);

  const kpis = useMemo(() => {
    return [
      { label: "Total Leads", value: meta?.total || paginationMeta.total || 0, icon: TbUsers, accent: "text-blue-400" },
      { label: "Potenciais BR", value: meta?.potential_br_total || 0, icon: TbUserSearch, accent: "text-emerald-400" },
      { label: "HOT", value: meta?.by_temperature?.HOT || 0, icon: TbAlertCircle, accent: "text-red-400" },
      { label: "Último Sync", value: meta?.last_sync_date || "—", icon: TbRefresh, accent: "text-amber-400" },
    ];
  }, [meta, paginationMeta.total]);

  const closeModal = () => {
    setModalOpen(false);
    setModalMode("create");
    setForm(emptyForm);
  };

  const handleCreate = () => {
    setModalMode("create");
    setForm(emptyForm);
    setModalOpen(true);
  };

  const handleEdit = (lead) => {
    setModalMode("edit");
    setForm({ ...emptyForm, ...lead });
    setModalOpen(true);
  };

  const handleView = (lead) => {
    setSelectedLead(lead);
    setDetailsOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const cleanPayload = (payload) => {
    const cleaned = { ...payload };
    ["drivers_count", "power_units"].forEach((field) => {
      if (cleaned[field] === "" || cleaned[field] === null || cleaned[field] === undefined) {
        cleaned[field] = null;
      }
    });
    return cleaned;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = cleanPayload(form);
      const response = modalMode === "create"
        ? await axios.post(API_BASE_URL, payload, { headers: getAuthHeaders() })
        : await axios.post(`${API_BASE_URL}/${form.id}`, payload, { headers: getAuthHeaders() });

      const saved = unwrapPayload(response);
      showSnackbar(modalMode === "create" ? "Lead Carrier criado com sucesso" : "Lead Carrier atualizado com sucesso", "success");
      closeModal();
      await fetchLeads(currentPage);
      await fetchMeta();
      if (selectedLead?.id === saved?.id) {
        setSelectedLead(saved);
      }
    } catch (error) {
      console.error("Error saving carrier lead:", error);
      showSnackbar(error.response?.data?.message || error.response?.data?.details || "Erro ao salvar Lead Carrier", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (lead, status) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/${lead.id}/status`, { status }, { headers: getAuthHeaders() });
      const updated = unwrapPayload(response);
      setLeads((prev) => prev.map((item) => (item.id === lead.id ? updated : item)));
      if (selectedLead?.id === lead.id) {
        setSelectedLead(updated);
      }
      await fetchMeta();
      showSnackbar(`Lead movido para ${status}`, "success");
    } catch (error) {
      console.error("Error updating carrier lead status:", error);
      showSnackbar("Erro ao atualizar status", "error");
    }
  };

  const handleNotesSave = async () => {
    if (!selectedLead) return;

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/${selectedLead.id}/notes`,
        { notes: selectedLead.notes || "" },
        { headers: getAuthHeaders() }
      );
      const updated = unwrapPayload(response);
      setSelectedLead(updated);
      setLeads((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      showSnackbar("Observações salvas", "success");
    } catch (error) {
      console.error("Error saving notes:", error);
      showSnackbar("Erro ao salvar observações", "error");
    }
  };

  const handleDelete = async (lead) => {
    const confirmed = window.confirm(`Delete the carrier lead "${lead.company || lead.mc_number || lead.id}"?`);
    if (!confirmed) return;

    try {
      await axios.delete(`${API_BASE_URL}/${lead.id}`, { headers: getAuthHeaders() });
      showSnackbar("Lead Carrier removido", "success");
      if (selectedLead?.id === lead.id) {
        setSelectedLead(null);
        setDetailsOpen(false);
      }
      await fetchLeads(currentPage);
      await fetchMeta();
    } catch (error) {
      console.error("Error deleting carrier lead:", error);
      showSnackbar("Erro ao remover Lead Carrier", "error");
    }
  };

  const handleFindDecisionMakers = async (lead) => {
    try {
      showSnackbar("Buscando decision makers...", "info");
      const response = await axios.post(`${API_BASE_URL}/${lead.id}/decision-makers`, {}, { headers: getAuthHeaders() });
      const payload = unwrapPayload(response);
      const updated = payload?.carrier || payload;
      setLeads((prev) => prev.map((item) => (item.id === lead.id ? updated : item)));
      setSelectedLead(updated);
      showSnackbar("Decision makers atualizados", "success");
    } catch (error) {
      console.error("Error finding decision makers:", error);
      showSnackbar("Erro ao buscar decision makers", "error");
    }
  };

  const handleSyncFmcsa = async () => {
    setSyncing(true);

    try {
      const payload = {
        max_records: 5000,
        batch_size: 1000,
        query: syncSearch || undefined,
        dot_number: syncDot || undefined,
        mc_number: syncMc || undefined,
      };
      const response = await axios.post(`${API_BASE_URL}/sync/fmcsa`, payload, { headers: getAuthHeaders() });
      const result = unwrapPayload(response);
      showSnackbar(`FMCSA concluído: ${result.inserted || 0} novos, ${result.updated || 0} atualizados, ${result.skipped || 0} ignorados`, "success");
      await fetchLeads(1);
      await fetchMeta();
      setCurrentPage(1);
    } catch (error) {
      console.error("Error syncing FMCSA:", error);
      showSnackbar(error.response?.data?.details || error.response?.data?.message || "Erro ao sincronizar FMCSA", "error");
    } finally {
      setSyncing(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("require_operational_profile", "true");
      const response = await axios.post(`${API_BASE_URL}/import-file`, formData, {
        headers: getAuthHeaders(true),
      });
      const result = unwrapPayload(response);
      showSnackbar(`Importação concluída: ${result.inserted || 0} novos, ${result.updated || 0} atualizados`, "success");
      await fetchLeads(1);
      await fetchMeta();
      setCurrentPage(1);
    } catch (error) {
      console.error("Error importing file:", error);
      showSnackbar(error.response?.data?.message || "Erro ao importar arquivo", "error");
    } finally {
      setImporting(false);
    }
  };

  const exportCSV = () => {
    const headers = [
      "Company",
      "Contact",
      "Phone",
      "Email",
      "MC",
      "DOT",
      "Location",
      "Lead Type",
      "Priority",
      "Score",
      "Temperature",
      "Status",
      "Source",
    ];

    const rows = leads.map((lead) => [
      lead.company,
      lead.name,
      lead.phone,
      lead.email,
      lead.mc_number,
      lead.dot_number,
      lead.current_location,
      lead.lead_type,
      lead.priority,
      lead.lead_score,
      lead.lead_temperature,
      lead.pipeline_status,
      lead.source,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `carrier-leads-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("Todos");
    setLeadTypeFilter("Todos");
    setTemperatureFilter("Todos");
    setPotentialOnly(false);
    setStateFilter("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
    showSnackbar("Filters clean", "info");
  };

  const statusBadgeClass = (status) => {
    const colors = {
      "Novo Lead": "bg-blue-500/10 text-blue-300 border-blue-500/20",
      "Não Atendida": "bg-zinc-500/10 text-zinc-300 border-zinc-500/20",
      "Em Contato": "bg-cyan-500/10 text-cyan-300 border-cyan-500/20",
      "Retorno Agendado": "bg-purple-500/10 text-purple-300 border-purple-500/20",
      Qualificado: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
      "Oferta Enviada": "bg-amber-500/10 text-amber-300 border-amber-500/20",
      Ativo: "bg-green-500/10 text-green-300 border-green-500/20",
      "Sem Interesse": "bg-orange-500/10 text-orange-300 border-orange-500/20",
      Descartado: "bg-red-500/10 text-red-300 border-red-500/20",
    };
    return colors[status] || "bg-zinc-500/10 text-zinc-300 border-zinc-500/20";
  };

  const temperatureBadgeClass = (temperature) => {
    const colors = {
      HOT: "bg-red-500/10 text-red-300 border-red-500/20",
      WARM: "bg-amber-500/10 text-amber-300 border-amber-500/20",
      COLD: "bg-sky-500/10 text-sky-300 border-sky-500/20",
    };
    return colors[temperature] || "bg-zinc-500/10 text-zinc-300 border-zinc-500/20";
  };

  const renderPagination = () => (
    <div className="flex flex-col gap-3 border-t border-zinc-700/70 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-zinc-400">
        Showing <span className="font-semibold text-white">{paginationMeta.from || 0}</span> to{" "}
        <span className="font-semibold text-white">{paginationMeta.to || 0}</span> of{" "}
        <span className="font-semibold text-white">{paginationMeta.total || 0}</span> carrier leads
      </div>

      <div className="flex items-center gap-2">
        <select
          value={itemsPerPage}
          onChange={(event) => {
            setItemsPerPage(Number(event.target.value));
            setCurrentPage(1);
          }}
          className="rounded-lg border border-zinc-700 bg-[#181714] px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>

        <button
          type="button"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage <= 1 || loading}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-700 bg-[#181714] text-zinc-300 transition hover:border-orange-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <span className="rounded-lg border border-zinc-700 bg-[#181714] px-3 py-2 text-sm text-zinc-300">
          Page <span className="font-semibold text-white">{currentPage}</span> / {totalPages}
        </span>

        <button
          type="button"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage >= totalPages || loading}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-700 bg-[#181714] text-zinc-300 transition hover:border-orange-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <AuthenticatedLayout>
      <div className="min-h-screen bg-[#11100e] px-4 py-6 text-white sm:px-6 lg:px-8">
        {snackbar && (
          <Snackbar
            key={snackbar.id}
            message={snackbar.message}
            type={snackbar.type}
            onClose={() => setSnackbar(null)}
          />
        )}

        <div className="mx-auto max-w-[1600px] space-y-6">
          <InfoSection
            title="Leads Carrier"
            description="Centralize carrier, broker and carrier/shipper leads imported from FMCSA, CSV or manual entry."
          />

          <GroupedContainer>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {kpis.map((kpi) => {
                const Icon = kpi.icon;
                return (
                  <div key={kpi.label} className="rounded-2xl border border-zinc-700/70 bg-[#181714] p-4 shadow-lg shadow-black/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{kpi.label}</p>
                        <p className="mt-2 text-2xl font-bold text-white">{kpi.value}</p>
                      </div>
                      <div className={`rounded-2xl bg-black/30 p-3 ${kpi.accent}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </GroupedContainer>

          <GroupedContainer>
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="grid flex-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-400">FMCSA name search</label>
                  <input
                    value={syncSearch}
                    onChange={(event) => setSyncSearch(event.target.value)}
                    placeholder="Company name"
                    className="w-full rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none transition focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-400">DOT</label>
                  <input
                    value={syncDot}
                    onChange={(event) => setSyncDot(event.target.value)}
                    placeholder="DOT number"
                    className="w-full rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none transition focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-400">MC</label>
                  <input
                    value={syncMc}
                    onChange={(event) => setSyncMc(event.target.value)}
                    placeholder="MC number"
                    className="w-full rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none transition focus:border-orange-500"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <SecondaryButton type="button" onClick={handleSyncFmcsa} disabled={syncing} className="w-full justify-center">
                    <TbRefresh className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                    {syncing ? "Syncing..." : "Sync FMCSA"}
                  </SecondaryButton>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <input ref={fileInputRef} type="file" accept=".csv,.txt,.json" onChange={handleImportFile} className="hidden" />
                <SecondaryButton type="button" onClick={handleImportClick} disabled={importing}>
                  <TbCloudUpload className="mr-2 h-4 w-4" />
                  {importing ? "Importing..." : "Import CSV/JSON"}
                </SecondaryButton>
                <SecondaryButton type="button" onClick={exportCSV}>
                  <TbDownload className="mr-2 h-4 w-4" />
                  Export CSV
                </SecondaryButton>
                <SecondaryButton type="button" onClick={handleCreate}>
                  <TbPlus className="mr-2 h-4 w-4" />
                  New Carrier Lead
                </SecondaryButton>
              </div>
            </div>
          </GroupedContainer>

          <GroupedContainer>
            <div className="grid gap-3 lg:grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr_0.7fr_0.7fr_auto]">
              <div className="relative">
                <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-500" />
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by company, phone, MC, DOT, location..."
                  className="w-full rounded-xl border border-zinc-700 bg-[#11100e] py-2 pl-10 pr-3 text-sm text-white outline-none transition focus:border-orange-500"
                />
              </div>

              <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setCurrentPage(1); }} className="rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none focus:border-orange-500">
                <option value="Todos">All Status</option>
                {PIPELINE_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>

              <select value={leadTypeFilter} onChange={(event) => { setLeadTypeFilter(event.target.value); setCurrentPage(1); }} className="rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none focus:border-orange-500">
                {LEAD_TYPES.map((type) => <option key={type} value={type}>{type === "Todos" ? "All Lead Types" : type}</option>)}
              </select>

              <select value={temperatureFilter} onChange={(event) => { setTemperatureFilter(event.target.value); setCurrentPage(1); }} className="rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none focus:border-orange-500">
                {TEMPERATURES.map((temp) => <option key={temp} value={temp}>{temp === "Todos" ? "All Scores" : temp}</option>)}
              </select>

              <input value={stateFilter} onChange={(event) => { setStateFilter(event.target.value.toUpperCase()); setCurrentPage(1); }} maxLength={2} placeholder="State" className="rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none focus:border-orange-500" />

              <button type="button" onClick={() => { setPotentialOnly((prev) => !prev); setCurrentPage(1); }} className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${potentialOnly ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 bg-[#11100e] text-zinc-300 hover:border-orange-500"}`}>
                Potential BR
              </button>

              <button type="button" onClick={clearFilters} className="inline-flex items-center justify-center rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-zinc-300 transition hover:border-orange-500 hover:text-white">
                <TbFilter className="mr-2 h-4 w-4" />
                Clear
              </button>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <input type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); setCurrentPage(1); }} className="rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none focus:border-orange-500" />
              <input type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); setCurrentPage(1); }} className="rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none focus:border-orange-500" />
            </div>
          </GroupedContainer>

          <GroupedContainer>
            <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
              {["Todos", ...PIPELINE_STATUSES].map((status) => {
                const active = statusFilter === status;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => { setStatusFilter(status); setCurrentPage(1); }}
                    className={`flex min-h-[42px] items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all sm:text-sm ${active ? "bg-orange-600 text-white shadow-sm" : "bg-[#11100e] text-zinc-400 hover:bg-zinc-800 hover:text-white"}`}
                  >
                    <span className="min-w-0 truncate">{status}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${active ? "bg-white/20 text-white" : "bg-zinc-800 text-zinc-300"}`}>{statusCounts[status] || 0}</span>
                  </button>
                );
              })}
            </div>

            <div className="overflow-hidden rounded-2xl border border-zinc-700/70 bg-[#181714]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-zinc-700/70">
                  <thead className="bg-[#11100e]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">MC / DOT</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Score</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-zinc-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-700/60">
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-zinc-400">Loading Carrier Leads...</td>
                      </tr>
                    ) : leads.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-zinc-400">No Carrier Leads found.</td>
                      </tr>
                    ) : leads.map((lead) => (
                      <tr key={lead.id} className="transition hover:bg-white/[0.03]">
                        <td className="px-4 py-4 align-top">
                          <button type="button" onClick={() => handleView(lead)} className="max-w-[260px] text-left text-sm font-semibold text-white hover:text-orange-300">
                            {lead.company || "—"}
                          </button>
                          <div className="mt-1 text-xs text-zinc-500">{lead.priority || "Sem prioridade"}</div>
                          {lead.authority_warning && <div className="mt-1 text-xs text-amber-300">SAFER/OA warning</div>}
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-zinc-300">
                          <div>{lead.mc_number || "—"}</div>
                          <div className="text-xs text-zinc-500">DOT {lead.dot_number || "—"}</div>
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-zinc-300">
                          <div>{lead.name || "Owner not identified"}</div>
                          <div className="mt-1 flex items-center gap-1 text-xs text-zinc-500"><TbPhone className="h-3.5 w-3.5" />{lead.phone || "—"}</div>
                          <div className="mt-1 flex items-center gap-1 text-xs text-zinc-500"><TbMail className="h-3.5 w-3.5" />{lead.email || "—"}</div>
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-zinc-300">{lead.current_location || [lead.city, lead.state].filter(Boolean).join(", ") || "—"}</td>
                        <td className="px-4 py-4 align-top">
                          <span className="rounded-full border border-zinc-600 bg-zinc-800/70 px-2.5 py-1 text-xs font-medium text-zinc-300">{lead.lead_type || "Carrier/Shipper"}</span>
                          <div className="mt-2 text-xs text-zinc-500">PU: {lead.power_units || "—"} · Drivers: {lead.drivers_count || "—"}</div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${temperatureBadgeClass(lead.lead_temperature)}`}>{lead.lead_temperature || "—"} · {lead.lead_score ?? 0}</span>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <select value={lead.pipeline_status || "Novo Lead"} onChange={(event) => handleStatusChange(lead, event.target.value)} className={`max-w-[180px] rounded-lg border px-2 py-1 text-xs font-semibold outline-none ${statusBadgeClass(lead.pipeline_status)}`}>
                            {PIPELINE_STATUSES.map((status) => <option key={status} value={status} className="bg-[#181714] text-white">{status}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => handleView(lead)} className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition hover:border-orange-500 hover:text-white" title="View"><TbTruck className="h-4 w-4" /></button>
                            <button type="button" onClick={() => handleEdit(lead)} className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition hover:border-orange-500 hover:text-white" title="Edit"><TbEdit className="h-4 w-4" /></button>
                            <button type="button" onClick={() => handleDelete(lead)} className="rounded-lg border border-red-900/60 p-2 text-red-300 transition hover:border-red-500 hover:text-red-200" title="Delete"><TbTrash className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </div>
          </GroupedContainer>
        </div>

        {detailsOpen && selectedLead && (
          <div className="fixed inset-0 z-[9998] flex justify-end bg-black/70 backdrop-blur-sm">
            <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-zinc-700 bg-[#181714] p-6 shadow-2xl">
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-orange-400">Carrier Lead</p>
                  <h2 className="mt-2 text-2xl font-bold text-white">{selectedLead.company || "—"}</h2>
                  <p className="mt-1 text-sm text-zinc-400">{selectedLead.current_location || "Location unavailable"}</p>
                </div>
                <button type="button" onClick={() => setDetailsOpen(false)} className="rounded-xl border border-zinc-700 p-2 text-zinc-300 hover:border-orange-500 hover:text-white"><TbX className="h-5 w-5" /></button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Detail label="Contact" value={selectedLead.name || "Owner not identified"} />
                <Detail label="Phone" value={selectedLead.phone || "—"} />
                <Detail label="Email" value={selectedLead.email || "—"} />
                <Detail label="MC / DOT" value={`${selectedLead.mc_number || "—"} / ${selectedLead.dot_number || "—"}`} />
                <Detail label="Lead Type" value={selectedLead.lead_type || "Carrier/Shipper"} />
                <Detail label="Status" value={selectedLead.pipeline_status || "Novo Lead"} />
                <Detail label="USDOT Status" value={selectedLead.usdot_status || "—"} />
                <Detail label="SAFER/OA" value={selectedLead.authority_status || "—"} />
                <Detail label="Power Units" value={selectedLead.power_units || "—"} />
                <Detail label="Drivers" value={selectedLead.drivers_count || "—"} />
                <Detail label="Priority" value={selectedLead.priority || "—"} />
                <Detail label="Score" value={`${selectedLead.lead_temperature || "—"} · ${selectedLead.lead_score ?? 0}`} />
              </div>

              {selectedLead.authority_warning_text && (
                <div className="mt-5 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                  {selectedLead.authority_warning_text}
                </div>
              )}

              <div className="mt-5 rounded-2xl border border-zinc-700 bg-[#11100e] p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-semibold text-white">Lead intelligence</h3>
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${temperatureBadgeClass(selectedLead.lead_temperature)}`}>{selectedLead.lead_temperature || "—"}</span>
                </div>
                <ul className="space-y-2 text-sm text-zinc-300">
                  {(selectedLead.lead_score_reasons || []).map((reason, index) => <li key={`${reason}-${index}`}>• {reason}</li>)}
                  {(!selectedLead.lead_score_reasons || selectedLead.lead_score_reasons.length === 0) && <li>No score reasons available.</li>}
                </ul>
              </div>

              <div className="mt-5 rounded-2xl border border-zinc-700 bg-[#11100e] p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-semibold text-white">Decision makers</h3>
                  <button type="button" onClick={() => handleFindDecisionMakers(selectedLead)} className="rounded-lg border border-orange-500/40 px-3 py-1.5 text-xs font-semibold text-orange-200 hover:bg-orange-500/10">Find</button>
                </div>
                <div className="space-y-3">
                  {(selectedLead.contacts || selectedLead.decision_makers || []).length === 0 ? (
                    <p className="text-sm text-zinc-500">No decision makers saved yet.</p>
                  ) : (selectedLead.contacts || selectedLead.decision_makers || []).map((contact) => (
                    <div key={contact.id || contact.name} className="rounded-xl border border-zinc-700 bg-[#181714] p-3 text-sm text-zinc-300">
                      <div className="font-semibold text-white">{contact.name || "Unnamed contact"}</div>
                      <div className="text-xs text-zinc-500">{contact.role || "Owner"} · confidence {contact.confidence || 0}%</div>
                      <div className="mt-2 text-xs text-zinc-400">{contact.phone || "—"} · {contact.email || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-zinc-700 bg-[#11100e] p-4">
                <label className="mb-2 block text-sm font-semibold text-white">Notes</label>
                <textarea
                  value={selectedLead.notes || ""}
                  onChange={(event) => setSelectedLead((prev) => ({ ...prev, notes: event.target.value }))}
                  rows={8}
                  className="w-full rounded-xl border border-zinc-700 bg-[#181714] px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
                />
                <div className="mt-3 flex justify-end">
                  <SecondaryButton type="button" onClick={handleNotesSave}>Save Notes</SecondaryButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {modalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm">
            <div ref={modalRef} className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-zinc-700 bg-[#181714] shadow-2xl">
              <div className="flex items-center justify-between border-b border-zinc-700 bg-[#11100e] px-6 py-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-orange-400">{modalMode === "create" ? "New" : "Edit"}</p>
                  <h2 className="text-xl font-bold text-white">Carrier Lead</h2>
                </div>
                <button type="button" onClick={closeModal} className="rounded-xl border border-zinc-700 p-2 text-zinc-300 hover:border-orange-500 hover:text-white"><TbX className="h-5 w-5" /></button>
              </div>

              <form onSubmit={handleSave} className="max-h-[calc(92vh-78px)] overflow-y-auto p-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <Field label="Company" required value={form.company} onChange={(value) => handleFieldChange("company", value)} />
                  <Field label="Contact / Owner" value={form.name || ""} onChange={(value) => handleFieldChange("name", value)} />
                  <Field label="Phone" value={form.phone || ""} onChange={(value) => handleFieldChange("phone", value)} />
                  <Field label="Email" type="email" value={form.email || ""} onChange={(value) => handleFieldChange("email", value)} />
                  <Field label="MC Number" value={form.mc_number || ""} onChange={(value) => handleFieldChange("mc_number", value)} />
                  <Field label="DOT Number" value={form.dot_number || ""} onChange={(value) => handleFieldChange("dot_number", value)} />
                  <Field label="City" value={form.city || ""} onChange={(value) => handleFieldChange("city", value)} />
                  <Field label="State" value={form.state || ""} maxLength={2} onChange={(value) => handleFieldChange("state", value.toUpperCase())} />
                  <Field label="ZIP" value={form.zip || ""} onChange={(value) => handleFieldChange("zip", value)} />
                  <Field label="Equipment" value={form.equipment || ""} onChange={(value) => handleFieldChange("equipment", value)} />
                  <Field label="Power Units" type="number" value={form.power_units || ""} onChange={(value) => handleFieldChange("power_units", value)} />
                  <Field label="Drivers" type="number" value={form.drivers_count || ""} onChange={(value) => handleFieldChange("drivers_count", value)} />
                  <SelectField label="Lead Type" value={form.lead_type || "Carrier/Shipper"} options={["Carrier/Shipper", "Broker", "Carrier/Broker"]} onChange={(value) => handleFieldChange("lead_type", value)} />
                  <SelectField label="Status" value={form.pipeline_status || "Novo Lead"} options={PIPELINE_STATUSES} onChange={(value) => handleFieldChange("pipeline_status", value)} />
                  <Field label="Source" value={form.source || ""} onChange={(value) => handleFieldChange("source", value)} />
                  <Field label="USDOT Status" value={form.usdot_status || ""} onChange={(value) => handleFieldChange("usdot_status", value)} />
                  <Field label="SAFER/OA Status" value={form.authority_status || ""} onChange={(value) => handleFieldChange("authority_status", value)} />
                  <Field label="Safety Rating" value={form.safety_rating || ""} onChange={(value) => handleFieldChange("safety_rating", value)} />
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-semibold text-zinc-300">Notes</label>
                  <textarea value={form.notes || ""} onChange={(event) => handleFieldChange("notes", event.target.value)} rows={6} className="w-full rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none focus:border-orange-500" />
                </div>

                <div className="mt-6 flex flex-col-reverse gap-3 border-t border-zinc-700 pt-4 sm:flex-row sm:justify-end">
                  <button type="button" onClick={closeModal} className="rounded-xl border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-300 hover:border-zinc-500 hover:text-white">Cancel</button>
                  <button type="submit" disabled={saving} className="rounded-xl bg-orange-600 px-5 py-2 text-sm font-bold text-white shadow-lg shadow-orange-950/30 transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60">
                    {saving ? "Saving..." : modalMode === "create" ? "Create Carrier Lead" : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

function Detail({ label, value }) {
  return (
    <div className="rounded-2xl border border-zinc-700 bg-[#11100e] p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className="mt-2 break-words text-sm font-semibold text-white">{value}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, maxLength }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-zinc-300">{label}{required && <span className="text-orange-400"> *</span>}</span>
      <input
        type={type}
        value={value}
        required={required}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none transition focus:border-orange-500"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-zinc-300">{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-xl border border-zinc-700 bg-[#11100e] px-3 py-2 text-sm text-white outline-none transition focus:border-orange-500">
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

export default CarrierLead;
